import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modify from 'ol/interaction/Modify';
import { never } from 'ol/events/condition';
import { Style, Circle as CircleStyle, Fill, Stroke } from 'ol/style';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';

import { useToast } from '@contexts/Toast.context';
import useNotify from '@hooks/useNotify';
import { detectCollection } from '@utils/api';
import { getOuterRing } from '@utils/map/helpers';
import { useObjectPickerStore } from '@stores/useObjectPickerStore';
import { invalidateCollectionResults } from '@utils/collectionQueries';
import { DETECTION_OUTCOME, classifyDetectionResult } from '@utils/detectionOutcome';

// Always-visible vertex handles rendered as a separate layer so the user can
// see all draggable points without needing to hover over the boundary.
const VERTEX_STYLE = new Style({
	image: new CircleStyle({
		radius: 6,
		fill: new Fill({ color: 'rgba(0, 100, 255, 0.9)' }),
		stroke: new Stroke({ color: 'rgba(0, 0, 0, 0.7)', width: 1.5 }),
	}),
});

/**
 * Builds Point features from a Polygon/MultiPolygon source's vertex
 * coordinates. Excludes the closing vertex (same as first) of each ring.
 */
const buildVertexFeatures = source => {
	const features = [];
	for (const f of source.getFeatures()) {
		const geometry = f.getGeometry();
		// MultiPolygon nests one level deeper than Polygon (rings-of-rings vs.
		// rings) — normalize to a flat list of rings either way.
		const polygonRings =
			geometry.getType() === 'MultiPolygon'
				? geometry.getCoordinates()
				: [geometry.getCoordinates()];
		for (const rings of polygonRings) {
			for (const ring of rings) {
				// Skip last coord (duplicate of first in a closed ring)
				for (let i = 0; i < ring.length - 1; i++) {
					features.push(new Feature(new Point(ring[i])));
				}
			}
		}
	}
	return features;
};

// Ids only have to be unique, never meaningful — see `sessionRef`.
let nextSessionId = 1;

/**
 * Drives "edit a boundary's vertices, then re-run detection with the edited
 * shape" for a single vector layer/source. Mirrors useAreaDraw's lifecycle
 * conventions (toolbar/interactions disabled while active, restored on exit)
 * but for modifying an existing polygon in place rather than drawing a new
 * one.
 *
 * Confirming the edit never starts a job outright. It previews the request (no
 * `confirm`), and the server reports what a run would do:
 *   - a run is needed  → `pendingConfirmation` is set so the caller can ask the
 *     user; `confirmRun()` then repeats the request with `confirm: true`, and
 *     `declineRun()` restores the original boundary and exits.
 *   - already detected → nothing to run; cached results are refreshed instead.
 *
 * Once a run does start, `activeJobUuid` is set in the global store so that
 * `useResumeDetectionJob` picks it up and shows the DetectionProgressModal —
 * consistent with the Object Picker flow.
 *
 * An edit only runs on a visible layer: `startEdit` refuses a hidden one, and the
 * caller keeps the toggle disabled while editing. This hook never changes visibility.
 *
 * @param {object} opts
 * @param {import('ol/layer/WebGLVector').default|null} opts.layer - layer whose source holds the boundary feature(s)
 * @param {import('ol/Map').default|null} opts.mapObject
 * @param {(action: object) => void} opts.dispatch - Project context dispatch
 * @param {string} opts.collectionUuid
 * @param {string} opts.projectUuid
 * @param {boolean} [opts.mapBusy] - another map tool owns the map; refuse to start an edit.
 *   True during this hook's own edit as well, so only check it before `startEdit`.
 * @returns {{ isEditing: boolean, isSaving: boolean, startEdit: () => void,
 *   cancelEdit: () => void, confirmEdit: () => void, pendingConfirmation: object|null,
 *   editedRing: Array<[number, number]>|null, confirmRun: () => void, declineRun: () => void }}
 */
export const useBoundaryEditor = ({
	layer,
	mapObject,
	dispatch,
	collectionUuid,
	projectUuid,
	mapBusy = false,
}) => {
	const { addToast } = useToast();
	const { checkForNotifications } = useNotify();
	const setActiveJobUuid = useObjectPickerStore(s => s.setActiveJobUuid);
	const queryClient = useQueryClient();

	const [isEditing, setIsEditing] = useState(false);
	// The server's un-confirmed verdict on the edited boundary, held while the
	// caller asks the user whether to run. `null` when nothing is awaiting an
	// answer. See `@utils/detectionOutcome`.
	const [pendingConfirmation, setPendingConfirmation] = useState(null);
	// Cloned pre-edit geometries keyed by feature reference, so Cancel can put
	// the boundary back. A Map avoids reliance on getFeatures() array index ordering.
	const originalGeometriesRef = useRef(new Map());
	// The open edit session, or `null` when none is — created in `startEdit`,
	// dropped in `endEdit`. Holds everything only true of one edit: an `id` that
	// travels with the mutation, the `collectionUuid` it started on (an edit belongs
	// to that collection even if the app moves off it), and the previewed `ring`,
	// re-sent on confirm so the run matches what the user saw rather than whatever
	// Modify has done to the source since.
	const sessionRef = useRef(null);
	// A response can land after its session ended — a collection switch, a cancel,
	// or the next edit. Dropping it keeps it from reopening the confirmation,
	// toasting about an edit the user left, or ending a later session.
	const isCurrentSession = id => sessionRef.current?.id === id;
	// The abandon effect below normally ends the edit the moment the collection
	// changes. This covers the sliver where a switch lands between a click and
	// its handler; it stays silent, because that effect's toast already explained.
	const isEditStale = () => sessionRef.current?.collectionUuid !== collectionUuid;

	// Owns the Modify interaction's lifecycle: added while editing, removed
	// (and other map interactions restored) the moment editing ends, whether
	// via Cancel, Confirm, or unmount. Also manages a vertex overlay layer that
	// renders all polygon vertices as always-visible circles.
	useEffect(() => {
		if (!isEditing || !layer || !mapObject) return;

		const source = layer.getSource();

		// Vertex overlay: a plain VectorLayer that draws Point features at each
		// polygon coordinate. Sits above the boundary so vertices are clickable.
		const vertexSource = new VectorSource({
			features: buildVertexFeatures(source),
		});
		const vertexLayer = new VectorLayer({
			source: vertexSource,
			style: VERTEX_STYLE,
			zIndex: 200,
		});
		mapObject.addLayer(vertexLayer);

		const modify = new Modify({
			source,
			deleteCondition: never,
		});
		mapObject.addInteraction(modify);

		// Keep the vertex overlay in sync as the user drags or inserts vertices.
		const syncVertices = () => {
			vertexSource.clear();
			vertexSource.addFeatures(buildVertexFeatures(source));
		};
		modify.on('modifyend', syncVertices);

		dispatch({ type: 'setToolBarVisible', payload: false });
		dispatch({ type: 'setInteractionsDisabled', payload: true });
		// Boundary-edit-specific flag so controls that must not run while an edit is
		// open (e.g. the AI Detection button) can gate on this alone, rather than on
		// the generic map-mode flags that every other tool also toggles.
		dispatch({ type: 'setBoundaryEditActive', payload: true });

		return () => {
			modify.un('modifyend', syncVertices);
			mapObject.removeInteraction(modify);
			mapObject.removeLayer(vertexLayer);
			dispatch({ type: 'setToolBarVisible', payload: true });
			dispatch({ type: 'setInteractionsDisabled', payload: false });
			dispatch({ type: 'setBoundaryEditActive', payload: false });
		};
	}, [isEditing, layer, mapObject, dispatch]);

	/**
	 * Clears the edit session's own state. Every exit goes through this, so a
	 * future piece of edit state only has to be forgotten in one place.
	 * Stable so the abandon effect below can depend on it honestly.
	 */
	const endEdit = useCallback(() => {
		sessionRef.current = null;
		setPendingConfirmation(null);
		setIsEditing(false);
	}, []);

	// A collection switch that leaves this component mounted — the picker, the
	// URL, a finishing job's auto-selection — arrives as a changed
	// `collectionUuid`. (A switch that unmounts the subtree instead, e.g. to a
	// collection whose data is still loading or has no objects, is handled by the
	// edit effect's cleanup above, silently.) Either way the edit is over: it
	// belongs to the collection it started on. Nothing is restored, because the
	// features belong to the collection just left and the source is replaced with
	// the new one's as soon as editing ends.
	useEffect(() => {
		if (!isEditing || sessionRef.current?.collectionUuid === collectionUuid) return;

		endEdit();
		addToast({
			id: 'boundary-edit-abandoned',
			bg: 'warning',
			title: 'Boundary edit discarded',
			message: 'The selected collection changed, so the unsaved boundary edit was discarded.',
			autohide: true,
		});
	}, [collectionUuid, isEditing, addToast, endEdit]);

	const { mutate: detectBoundary, isPending: isSaving } = useMutation({
		mutationFn: ({ uuid, aoi, confirm }) =>
			detectCollection(uuid, { projectUuid, aoi, confirm }),
		onSuccess: (result, { session }) => {
			// An abandoned run's job is still surfaced by `checkForNotifications()`
			// in onSettled, which stays unguarded.
			if (!isCurrentSession(session)) return;

			const outcome = classifyDetectionResult(result);

			if (outcome === DETECTION_OUTCOME.NEEDS_CONFIRMATION) {
				// Nothing started. Hold the edit open behind the confirmation so the
				// user can approve the run or back out of it.
				setPendingConfirmation(result);
				return;
			}

			setPendingConfirmation(null);

			// `null` means the response matched no known outcome. Mirror `onError`:
			// report it and stay in edit mode so the user can retry or cancel out.
			// Reporting "already analyzed" here would discard the edit on a guess.
			if (outcome === null) {
				addToast({
					id: 'boundary-edit-error',
					bg: 'danger',
					title: 'Error updating boundary',
					message:
						'The detection service returned an unexpected response. Please try again.',
					autohide: false,
				});
				return;
			}

			if (outcome === DETECTION_OUTCOME.STARTED) {
				setActiveJobUuid(result?.job_uuid ?? null);
				addToast({
					id: 'boundary-edit-success',
					bg: 'success',
					title: 'Updating boundary',
					message:
						"Detection is re-running with the updated boundary. You'll be notified when it's complete.",
					autohide: true,
				});
			} else {
				// Already detected — no job runs. Invalidate cached results so the
				// map reflects the current detection data.
				invalidateCollectionResults(queryClient);
				addToast({
					id: 'boundary-edit-success',
					bg: 'success',
					title: 'Boundary updated',
					message: 'This area was already analyzed. Loading existing results.',
					autohide: true,
				});
			}
			endEdit();
		},
		onError: (error, { session }) => {
			if (!isCurrentSession(session)) return;

			addToast({
				id: 'boundary-edit-error',
				bg: 'danger',
				title: 'Error updating boundary',
				message: error?.message ?? 'Unknown error updating boundary.',
				autohide: false,
			});
			// Close any confirmation but stay in edit mode, so the user can retry
			// Confirm or Cancel out.
			setPendingConfirmation(null);
		},
		onSettled: () => checkForNotifications(),
	});

	/**
	 * Sends a detect request for the open session, so every request carries the
	 * session it belongs to and targets the collection the edit started on — never
	 * whatever the app has moved to since.
	 */
	const sendDetect = ({ aoi, confirm }) => {
		if (!sessionRef.current) return;

		const { id, collectionUuid: uuid } = sessionRef.current;
		detectBoundary({ session: id, uuid, aoi, confirm });
	};

	const startEdit = () => {
		// Two editing interactions would share pointer events per-pixel and clobber
		// each other's mode flags on exit; a hidden boundary would drag vertices
		// nobody can see. The button is disabled for both — this guards the state
		// flipping after render.
		if (mapBusy || !layer?.getVisible()) return;

		const source = layer.getSource();
		if (!source) return;
		const map = new Map();
		source.getFeatures().forEach(f => map.set(f, f.getGeometry().clone()));
		originalGeometriesRef.current = map;
		sessionRef.current = { id: nextSessionId++, collectionUuid, ring: null };
		setPendingConfirmation(null);
		setIsEditing(true);
	};

	/**
	 * Put the boundary back as it was when editing started and leave edit mode.
	 * Shared by Cancel and by declining the confirmation — both mean "forget this
	 * edit", so they must restore identically.
	 */
	const discardEdit = () => {
		const source = layer?.getSource();
		source?.getFeatures().forEach(f => {
			const original = originalGeometriesRef.current.get(f);
			if (original) f.setGeometry(original);
		});
		endEdit();
	};

	const cancelEdit = () => discardEdit();

	const confirmEdit = () => {
		if (isEditStale()) return;

		const source = layer?.getSource();
		const features = source?.getFeatures() ?? [];

		// Dirty check: skip the re-run if the user hasn't actually moved any
		// vertices. Compare each feature's current coordinates against the
		// snapshot taken when editing started.
		const hasChanges = features.some(f => {
			const original = originalGeometriesRef.current.get(f);
			if (!original) return true;
			const currentCoords = JSON.stringify(f.getGeometry().getCoordinates());
			const originalCoords = JSON.stringify(original.getCoordinates());
			return currentCoords !== originalCoords;
		});

		if (!hasChanges) {
			addToast({
				id: 'boundary-no-changes',
				bg: 'success',
				title: 'No changes made',
				message: 'The boundary was not modified.',
				autohide: true,
			});
			endEdit();
			return;
		}

		const ring = features.length === 1 ? getOuterRing(features[0].getGeometry()) : null;

		if (!ring) {
			addToast({
				id: 'boundary-edit-error',
				bg: 'danger',
				title: 'Cannot save boundary',
				message:
					features.length === 1
						? 'This boundary shape could not be saved.'
						: 'Editing a multi-part boundary is not supported yet.',
				autohide: false,
			});
			return;
		}

		// Phase 1: ask the server what re-running on this shape would do.
		sessionRef.current.ring = ring;
		sendDetect({ aoi: ring, confirm: false });
	};

	/** Phase 2: the user approved the run — start it on the previewed shape. */
	const confirmRun = () => {
		if (isEditStale()) return;

		const ring = sessionRef.current?.ring;
		if (!ring) return;
		sendDetect({ aoi: ring, confirm: true });
	};

	/** The user declined: forget the edit and restore the original boundary. */
	const declineRun = () => discardEdit();

	return {
		isEditing,
		isSaving,
		startEdit,
		cancelEdit,
		confirmEdit,
		pendingConfirmation,
		editedRing: sessionRef.current?.ring ?? null,
		confirmRun,
		declineRun,
	};
};
